import { Dispatch, SetStateAction, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { PAYMENT_CONFIG } from 'state/paymentConfig/type';
import { toast } from 'react-toastify';
import { STORAGE_KEYS, PROCESSOR_NAMES } from 'utils/constants';
import {
  TFI_PROCESSOR_ID,
  TFI_ASYNC_MODE_CODE,
} from 'constants/settings/internalConfig';
import { isProcessorType, isAeropayOrBarterpay } from 'utils/processor';
import { isValidUUID } from 'utils/validation';

const errorRawToString = (errorRaw: unknown): string => {
  if (typeof errorRaw === 'string') return errorRaw;
  if (errorRaw && typeof errorRaw === 'object') {
    const obj = errorRaw as { message?: unknown; data?: unknown };
    if (typeof obj.message === 'string') return obj.message;
    if (typeof obj.data === 'string') return obj.data;
  }
  return '';
};

type UpdatePaymentSolutionsFn = (
  values: PAYMENT_CONFIG.IUpdatePaymentSolutions
) => Promise<PAYMENT_CONFIG.IUpdatePaymentSolutionsAPIResponse | null>;

type FetchCardReaderValuesFn = (
  id?: string
) => Promise<PAYMENT_CONFIG.IGetCardReaderList | undefined>;

type SaveGlobalCardReaderValuesFn = (
  values: PAYMENT_CONFIG.IGlobalCardReader
) => Promise<void>;

interface UseProcessorActionsProps {
  toUpdatePaymentSolutions: UpdatePaymentSolutionsFn;
  toDeletePaymentSolutions: UpdatePaymentSolutionsFn;
  toFetchAllPaymentSolutions: () => void;
  toFetchCardReaderValues: FetchCardReaderValuesFn;
  saveGlobalCardReaderValues: SaveGlobalCardReaderValuesFn;
  updateGlobalCardReaderValues: SaveGlobalCardReaderValuesFn;
  merchantIds: { [processorId: string]: string };
  setMerchantIds: Dispatch<SetStateAction<{ [processorId: string]: string }>>;
  setDeletedProcessors: Dispatch<SetStateAction<Set<string>>>;
}

export const useProcessorActions = ({
  toUpdatePaymentSolutions,
  toDeletePaymentSolutions,
  toFetchAllPaymentSolutions,
  toFetchCardReaderValues,
  saveGlobalCardReaderValues,
  updateGlobalCardReaderValues,
  merchantIds,
  setMerchantIds,
  setDeletedProcessors,
}: UseProcessorActionsProps) => {
  // Processor whose enable/disable request is in flight, so its toggle can be
  // disabled — prevents rapid repeat clicks from firing stacked toasts.
  const [togglingProcessorId, setTogglingProcessorId] = useState<string | null>(
    null
  );

  const ensureTfiAsyncModeEnabled = async () => {
    const cardReaderData = await toFetchCardReaderValues(TFI_PROCESSOR_ID);
    const existing = cardReaderData?.items?.find(
      (item) =>
        item.code === TFI_ASYNC_MODE_CODE &&
        item.processorId === TFI_PROCESSOR_ID
    );

    if (existing) {
      if (existing.value === 'true') return;
      await updateGlobalCardReaderValues({
        name: existing.name,
        category: existing.category,
        code: existing.code,
        value: 'true',
        isSecret: existing.isSecret,
        isRequired: existing.isRequired,
        id: existing.id,
        processorId: existing.processorId,
      });
    } else {
      await saveGlobalCardReaderValues({
        name: `TFI_${TFI_ASYNC_MODE_CODE}`,
        category: null,
        code: TFI_ASYNC_MODE_CODE,
        value: 'true',
        isSecret: false,
        isRequired: false,
        id: uuidv4(),
        processorId: TFI_PROCESSOR_ID,
      });
    }

    // The save/update hooks swallow API errors, so verify the write actually
    // persisted. The write is eventually consistent — retry the read-back
    // before declaring failure.
    for (let attempt = 1; attempt <= 3; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, 500));
      const verifyData = await toFetchCardReaderValues(TFI_PROCESSOR_ID);
      const saved = verifyData?.items?.find(
        (item) =>
          item.code === TFI_ASYNC_MODE_CODE &&
          item.processorId === TFI_PROCESSOR_ID
      );
      if (saved?.value === 'true') {
        return;
      }
    }
    throw new Error('tfiAsyncMode was not persisted as true');
  };

  const getEntityId = () => {
    const entity_id = localStorage.getItem(STORAGE_KEYS.ENTITY_ID);
    if (!entity_id) {
      toast.error('Entity ID not found in localStorage. Please reload the page.');
      return null;
    }
    return entity_id;
  };

  const handleToggle = async (row: PAYMENT_CONFIG.IProcessors) => {
    // Ignore clicks while a toggle request is already in flight.
    if (togglingProcessorId) return;
    setTogglingProcessorId(row.processorId);
    try {
      const entity_id = getEntityId();
      if (!entity_id) return;

      const newStatus = !row.isEnabled;

      // Validate barterpay UUID format before sending
      const isBarterpay = isProcessorType(row?.processorName, PROCESSOR_NAMES.BARTERPAY);
      const merchantId = merchantIds?.[row.processorId];

      if (isBarterpay && merchantId && !isValidUUID(merchantId)) {
        toast.error('Invalid UUID format. Please enter a valid UUID (e.g., 123e4567-e89b-12d3-a456-426614174000)');
        return;
      }

      const processorData = {
        processorId: row.processorId,
        processorName: row.processorName,
        isEnabled: newStatus,
        ...(isAeropayOrBarterpay(row?.processorName) && merchantId ? { merchantId } : {}),
      };

      toast.warn(`${newStatus ? 'Enabling' : 'Disabling'} ${row.processorName}...`);

      const response = await toUpdatePaymentSolutions({
        entityId: entity_id,
        processor: {
          processors: [processorData],
        },
      });

      // Validate response exists
      if (!response) {
        toast.error(`Failed to update ${row.processorName}`);
        console.error('No response from API');
        toFetchAllPaymentSolutions();
        return;
      }

      // Check for errors first (errorRaw indicates failure)
      if (response.errorRaw) {
        toast.error(
          errorRawToString(response.errorRaw) ||
            response.message ||
            `Failed to update ${row.processorName}`
        );
        console.error('API error:', response);
        toFetchAllPaymentSolutions();
        return;
      }

      // Check webhook validation failure
      if (newStatus && response?.data?.webhookValidated === false) {
        toast.error(response.message || `Failed to enable ${row.processorName}`);
        toFetchAllPaymentSolutions();
        return;
      }

      // Validate success response structure
      if (
        response?.data?.processor?.processors &&
        Array.isArray(response.data.processor.processors)
      ) {
        const updatedProcessor = response.data.processor.processors.find(
          (proc) => proc.processorId === row.processorId
        );

        if (updatedProcessor) {
          if (
            updatedProcessor.isEnabled &&
            isProcessorType(row.processorName, PROCESSOR_NAMES.TFI)
          ) {
            try {
              await ensureTfiAsyncModeEnabled();
            } catch (err) {
              console.error('Failed to auto-enable tfiAsyncMode:', err);
              toast.warn(
                `Enabled ${row.processorName}, but failed to enable async mode. Please enable it manually in Feature Flags.`
              );
              return;
            }
          }
          toast.success(`Successfully ${updatedProcessor.isEnabled ? 'enabled' : 'disabled'} ${row.processorName}`);
        } else {
          toast.error(`Processor ${row.processorName} not found in response`);
          toFetchAllPaymentSolutions();
        }
      } else {
        toast.error(`Invalid response structure from server`);
        console.error('API response invalid:', response);
        toFetchAllPaymentSolutions();
      }
    } catch (error: unknown) {
      const err = error as {
        response?: { data?: { errorRaw?: unknown } };
        message?: string;
      };
      const errorMessage =
        errorRawToString(err?.response?.data?.errorRaw) ||
        err?.message ||
        `Error updating ${row.processorName}`;

      toast.error(errorMessage);
      console.error('Error updating toggle:', error);
    } finally {
      setTogglingProcessorId(null);
    }
  };

  const handleDelete = async (row: PAYMENT_CONFIG.IProcessors) => {
    try {
      const entity_id = getEntityId();
      if (!entity_id) return;

      toast.warn(`Deleting ${row.processorName}...`);

      const response = await toDeletePaymentSolutions({
        entityId: entity_id,
        processor: {
          processors: [
            {
              processorId: row.processorId,
            },
          ],
        },
      });

      // Check for errors first
      if (response?.errorRaw) {
        toast.error(
          errorRawToString(response.errorRaw) ||
            response.message ||
            `Failed to delete ${row.processorName}`
        );
        console.error('API error:', response);
        toFetchAllPaymentSolutions();
        return;
      }

      if (response?.data?.processor?.processors?.length) {
        setDeletedProcessors((prev) => {
          const newSet = new Set(prev);
          newSet.add(row.processorId);
          return newSet;
        });

        setMerchantIds((prev) => {
          const updated = { ...prev };
          delete updated[row.processorId];
          return updated;
        });

        toFetchAllPaymentSolutions();

        toast.success(`Successfully deleted ${row.processorName}`);
      } else {
        toast.error(`Failed to delete ${row.processorName}`);
        toFetchAllPaymentSolutions();
      }
    } catch (error) {
      toast.error(`Error deleting ${row.processorName}`);
      console.error('Error deleting processor:', error);
      toFetchAllPaymentSolutions();
    }
  };

  const handleActivate = async (row: PAYMENT_CONFIG.IProcessors) => {
    try {
      const entity_id = getEntityId();
      if (!entity_id) return;

      const isAeropay = isProcessorType(row.processorName, PROCESSOR_NAMES.AEROPAY);
      const isBarterpay = isProcessorType(row.processorName, PROCESSOR_NAMES.BARTERPAY);
      const merchantId = (merchantIds?.[row.processorId] || '').trim();

      if (isBarterpay && merchantId && !isValidUUID(merchantId)) {
        toast.error('Invalid UUID format. Please enter a valid UUID (e.g., 123e4567-e89b-12d3-a456-426614174000)');
        return;
      }

      toast.warn(`Activating ${row.processorName}...`);

      const processorData = {
        processorId: row.processorId,
        processorName: row.processorName,
        isEnabled: false,
        ...((isAeropay || isBarterpay) && merchantId ? { merchantId } : {}),
      };

      const response = await toUpdatePaymentSolutions({
        entityId: entity_id,
        processor: {
          processors: [processorData],
        },
      });

      if (!response) {
        toast.error(`Failed to activate ${row.processorName}`);
        console.error('No response from API');
        toFetchAllPaymentSolutions();
        return;
      }

      // Check for errors first (errorRaw indicates failure)
      if (response.errorRaw) {
        toast.error(
          errorRawToString(response.errorRaw) ||
            response.message ||
            `Failed to activate ${row.processorName}`
        );
        console.error('API error:', response);
        toFetchAllPaymentSolutions();
        return;
      }

      if (
        response?.data?.processor?.processors &&
        Array.isArray(response.data.processor.processors)
      ) {
        const updatedProcessor = response.data.processor.processors.find(
          (proc) => proc.processorId === row.processorId
        );

        if (updatedProcessor) {
          setDeletedProcessors((prev) => {
            const newSet = new Set(prev);
            newSet.delete(row.processorId);
            return newSet;
          });

          setMerchantIds((prev) => {
            const updated = { ...prev };
            delete updated[row.processorId];
            return updated;
          });

          if (isProcessorType(row.processorName, PROCESSOR_NAMES.TFI)) {
            try {
              await ensureTfiAsyncModeEnabled();
            } catch (err) {
              console.error('Failed to auto-enable tfiAsyncMode:', err);
              toast.warn(
                `Activated ${row.processorName}, but failed to enable async mode. Please enable it manually in Feature Flags.`
              );
              return;
            }
          }

          toast.success(`Successfully activated ${row.processorName}`);
        } else {
          toast.error(`Processor ${row.processorName} not found in response`);
          toFetchAllPaymentSolutions();
        }
      } else {
        toast.error(`Invalid response structure from server`);
        console.error('API response invalid:', response);
        toFetchAllPaymentSolutions();
      }
    } catch (error) {
      toast.error(`Error activating ${row.processorName}`);
      console.error('Error activating processor:', error);
    }
  };

  return {
    handleToggle,
    handleDelete,
    handleActivate,
    togglingProcessorId,
  };
};
